import {
  ChangeDetectorRef,
  Component,
  effect,
  NgZone,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core'
import { RetailComponent } from 'app/mobility/retail/retail.component'
import { MobilityService } from 'app/services/mobility.service'
import { AuthenticationService, ContextService } from 'app/services'
import { DomSanitizer, Title } from '@angular/platform-browser'
import { IsolinesService } from 'app/services/isolines.service'
import { TranslateService } from '@ngx-translate/core'
import { HttpClient } from '@angular/common/http'
import { Delivery, Vertical } from 'app/models/Vertical'
import { Colors } from 'app/common/config/constants'
import { filter, firstValueFrom, merge, of } from 'rxjs'
import { debounceTime, switchMap } from 'rxjs/operators'
import { toObservable } from '@angular/core/rxjs-interop'
import { ModalStatus } from '../../common/modal/modal.component'
import { environment } from '../../../environments/environment'
import { injectDeleteCampaignMutation } from './models/campaign.model'
import { injectCampaignsQuery } from './models/campaigns.model'
import { injectMetadataQuery } from '../ooh/model/metadata.model'
import {
  type PoisFilters,
  type PoiRow,
  injectPoisQuery,
} from '../ooh/model/pois.model'
import { injectStreetsQuery, StreetsFilters } from '../ooh/model/streets.model'
import { injectTrafficQuantilesQuery } from '../ooh/model/traffic-quantiles.model'
import {
  injectAggregationsQuery,
  type AggregationsFilters,
} from '../ooh/model/aggregations.model'
import OOHClusterLayer from '../ooh/OOHClusterLayer'
import {
  injectCompareDeliveriesQuery,
  type CompareDeliveriesFilters,
} from '../ooh/model/compare-deliveries.model'
import {
  injectCampaignDemographicsQuery,
  type CampaignDemographicsFilters,
} from './models/campaigns-demographics.model'
import {
  injectCompareAggregationsQuery,
  type CompareAggregationsFilters,
} from './models/compare-aggregations.model'
import { buildSingleStatWidget } from './helpers/campaigns-widgets.helper'
import {
  buildRanking,
  buildVisitorStats,
  buildDemographicsStats,
  safeJsonParse,
  sum,
} from './helpers/campaigns-stats.helper'
import {
  loadCampaignMarkerIcons,
  buildQuartileMarkerExpression,
} from './helpers/campaigns-map.helper'
import { downloadCampaignCSV } from './helpers/campaigns-csv.helper'
import {
  parsePoiRows,
  buildPoiListItems,
  getColumnsFromTargetFilter,
} from '../ooh/helpers/ooh-stats.helper'
import { injectCartoClient } from '../ooh/services/carto.helper'
import { fetchAggregations } from '../ooh/services/aggregations.service'

@Component({
  standalone: false,
  selector: 'app-campaigns',
  templateUrl: './campaigns.component.html',
  styleUrls: [
    './../mobility/mobility.component.scss',
    './../retail/retail.component.scss',
    './campaigns.component.scss',
  ],
})
export class CampaignsComponent
  extends RetailComponent
  implements OnInit, OnDestroy
{
  campaigns = []
  currentCampaign = null
  override showStats = false
  private statsShownOnce = false

  campaignsQuery = injectCampaignsQuery()
  private metadataQuery = injectMetadataQuery()
  private deleteCampaignMutation = injectDeleteCampaignMutation()
  private campaignsLoaded = false
  private initialFlyDone = false

  poisFilters = signal<PoisFilters>({
    delivery_id: 0,
    delivery_start_date: '',
    filter_combined: 'All-All',
    tag_groups: [],
  })
  poisQuery = injectPoisQuery(this.poisFilters)
  private poisData$ = toObservable(this.poisQuery.data)
  clusterLayer: OOHClusterLayer = null

  comparePoisFilters = signal<PoisFilters>({
    delivery_id: 0,
    delivery_start_date: '',
    filter_combined: 'All-All',
    tag_groups: [],
  })
  comparePoisQuery = injectPoisQuery(this.comparePoisFilters)

  streetsFilters = signal<StreetsFilters>({
    delivery_id: 0,
    filter_combined: 'All-All',
  })
  streetsQuery = injectStreetsQuery(this.streetsFilters)
  trafficQuantilesQuery = injectTrafficQuantilesQuery(this.poisFilters)

  aggregationsFilters = signal<AggregationsFilters>({
    delivery_id: 0,
    delivery_start_date: '',
    filter_combined: 'All-All',
    columns: [],
  })
  aggregationsQuery = injectAggregationsQuery(this.aggregationsFilters)

  demographicsFilters = signal<CampaignDemographicsFilters>({
    delivery_id: 0,
    delivery_start_date: '',
    filter_combined: 'All-All',
  })
  demographicsQuery = injectCampaignDemographicsQuery(this.demographicsFilters)

  compareDemographicsFilters = signal<CampaignDemographicsFilters>({
    delivery_id: 0,
    delivery_start_date: '',
    filter_combined: 'All-All',
  })
  compareDemographicsQuery = injectCampaignDemographicsQuery(
    this.compareDemographicsFilters,
  )

  compareAggregationsFilters = signal<CompareAggregationsFilters>({
    delivery_id_1: 0,
    delivery_id_2: 0,
    delivery_start_date_1: '',
    delivery_start_date_2: '',
    filter_combined: 'All-All',
    columns: [],
  })
  compareAggregationsQuery = injectCompareAggregationsQuery(
    this.compareAggregationsFilters,
  )

  compareDeliveriesFilters = signal<CompareDeliveriesFilters>({
    delivery_id_1: 0,
    delivery_start_date_1: '',
    delivery_id_2: 0,
    delivery_start_date_2: '',
    filter_combined: 'All-All',
    tag_groups: [],
  })
  compareDeliveriesQuery = injectCompareDeliveriesQuery(
    this.compareDeliveriesFilters,
  )

  private cartoClient = injectCartoClient()

  private metadataEffect = effect(() => {
    const orgs = this.metadataQuery.data()
    if (!orgs?.length || this.context.vertical?.isOOH()) return

    const org = orgs[0]
    const verticalData = org.config.verticals[0]
    const parameters = org.config.parameters.default

    const delivery = new Delivery({ config: parameters })
    delivery.territory = {
      name: verticalData.territories[0],
      id: org.terrunit_region_id,
      bbox: [],
      region_id: org.terrunit_region_id,
      terrunit: 0,
    }

    const vertical = new Vertical({
      id: verticalData.id,
      slug: 'ooh',
      title: verticalData.title,
      territories: verticalData.territories,
    })
    vertical.delivery = delivery
    vertical.deliveries = {
      [verticalData.territories[0]]: [delivery],
    }

    this.context.organization = {
      id: org.org_id,
      name: org.name,
      terrunit_region_id: org.terrunit_region_id,
      config: org.config,
    }

    this.context.resetContext(vertical)
    this.context.$currentVertical.next(vertical)
    this.context.$verticals.next([vertical])
    this.changes.detectChanges()
  })
  private campaignsEffect = effect(() => {
    const rows = this.campaignsQuery.data()
    if (!rows?.length) return
    const isInitialLoad = !this.campaignsLoaded
    this.campaignsLoaded = true
    this.campaigns = rows
      .map(
        (row) =>
          new Delivery({
            id: row.id,
            parent_id: row.parent_id,
            name: row.name,
            start: row.start,
            finish: row.finish,
            granularity: row.granularity,
            territory: safeJsonParse(row.territory),
            type: row.type,
            config: safeJsonParse(row.config),
          }),
      )
      .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
    if (isInitialLoad) {
      this.currentCampaign = this.campaigns[0]
      this.updatePoisFilters()
      if (this.mapLoaded) {
        this.doComponentChanges()
      }
    }
    this.changes.markForCheck()
  })

  private poisEffect = effect(() => {
    const rows = this.poisQuery.data()
    if (!rows?.length) return
    this._loadPoisData(rows)
    this._processStats()
    this._processPoiList()
    this.isLoading =
      this.poisQuery.isFetching() || this.aggregationsQuery.isFetching()

    if (
      !this.initialFlyDone &&
      this.mapLoaded &&
      this.map?.map &&
      this.currentCampaign?.territory?.bbox
    ) {
      this.initialFlyDone = true
      this.map.flyToBbox(this.currentCampaign.territory.bbox, {
        padding: 150,
        pitch: 0,
        speed: 0.5,
      })
    }

    this.changes.markForCheck()
  })

  private fetchingEffect = effect(() => {
    const fetching =
      this.poisQuery.isFetching() ||
      this.poisQuery.isLoading() ||
      this.aggregationsQuery.isFetching() ||
      this.compareAggregationsQuery.isFetching()
    if (fetching) {
      this.isLoading = true
      this.changes.markForCheck()
    }
  })

  private streetsEffect = effect(() => {
    const geojson = this.streetsQuery.data()
    const stops = this.trafficQuantilesQuery.data()
    if (!geojson || !stops || !this.hasTrafficData || !this.map?.map) return
    this.paintDataTrafficLayer()
  })

  private aggregationsEffect = effect(() => {
    const aggs = this.aggregationsQuery.data()
    if (!aggs) return

    this.mobilityStats = {
      ...this.mobilityStats,
      globalAggs: aggs,
      ...(this.selectedCompareDelivery
        ? {}
        : {
            visitors: aggs.sum,
            visitorsSum: aggs.sum,
            visitorsAbs: aggs.sum,
            visitorsAvg: aggs.avg,
          }),
    }
    this.changes.detectChanges()
  })

  private demographicsEffect = effect(() => {
    const demo = this.demographicsQuery.data()
    const compDemo = this.compareDemographicsQuery.data()
    // Read the signal so the effect re-fires when compare filters change
    const compEnabled = this.compareDemographicsFilters().delivery_id > 0
    if (!demo) return

    const demographics = buildDemographicsStats(demo, compDemo, compEnabled)
    this.mobilityStats = { ...this.mobilityStats, ...demographics }
    this.changes.detectChanges()
  })

  private compareAggregationsEffect = effect(() => {
    const rows = this.compareAggregationsQuery.data()
    if (!rows?.length || !this.selectedCompareDelivery) return

    const refId = Number(this.currentCampaign?.id)
    const ref = rows.find((r) => r.delivery_id === refId)
    const comp = rows.find((r) => r.delivery_id !== refId)
    if (!ref || !comp) return

    this.mobilityStats = {
      ...this.mobilityStats,
      visitorsSum: [ref.nvisitors_sum, comp.nvisitors_sum],
      visitorsAbs: [ref.nvisitors_sum, comp.nvisitors_sum],
      visitorsAvg: [ref.nvisitors_avg, comp.nvisitors_avg],
      traffic: [ref.traffic_sum, comp.traffic_sum],
    }
    this.isLoading = false
    this.changes.detectChanges()
  })

  private comparePoisEffect = effect(() => {
    const rows = this.comparePoisQuery.data()
    if (!rows?.length) return
    // Rebuild ranking with compare data as second entry
    this._processStats()
    this.changes.markForCheck()
  })

  private compareDeliveriesEffect = effect(() => {
    const rows = this.compareDeliveriesQuery.data()
    if (!rows?.length || !this.selectedCompareDelivery) return

    // Load compare delivery POIs into cluster layer for hover comparison
    this.allPois = parsePoiRows(rows)
    this._processPoiList()
    this.changes.detectChanges()
  })

  private showStatsEffect = effect(() => {
    const hasPois = !!this.poisQuery.data()?.length
    const hasAggs = !!this.aggregationsQuery.data()
    if (!this.statsShownOnce && hasPois && hasAggs) {
      this.statsShownOnce = true
      this.showStats = true
      this.changes.markForCheck()
    }
  })

  colorMen = Colors.colorMen
  colorWomen = Colors.colorWomen
  legendLabels = []
  showRankingPopup = false
  showDeleteConfirmModal = false
  deleteCampaignLoading = false

  trafficDataId = 'ooh-traffic-tileset-data'

  hoverPopupData = {
    top: 0,
    left: 0,
    show: false,
    data: null,
    primary: false,
    showFrom: false,
    tooltip: null,
    tooltipExtra: null,
    updated: false,
  }

  showOnlyCommonPOIs = false

  get deleteConfirmModalStatus() {
    if (!this.showDeleteConfirmModal) {
      return ModalStatus.Hidden
    }
    return ModalStatus.Active
  }

  get compareLabels() {
    return []
  }

  get genderCompareLabel() {
    if (this.selectedCompareDelivery) {
      return [this.currentCampaign.name, this.selectedCompareDelivery.name]
    }
    return []
  }

  get selectedPOI() {
    return null
  }

  get campaignPOIs() {
    const data =
      this.mobilityStats &&
      this.mobilityStats.ranking &&
      this.mobilityStats.ranking.data
    if (!data || !data[0]) {
      return []
    }
    return data[0].list
  }

  get comparedCampaignPOIs() {
    const data =
      this.mobilityStats &&
      this.mobilityStats.ranking &&
      this.mobilityStats.ranking.data
    if (!data || !data[1]) {
      return []
    }
    return data[1].list
  }

  get sharedCampaignPOIs() {
    return this.campaignPOIs.filter((p1) =>
      this.comparedCampaignPOIs.some((p2) => p2.id === p1.id),
    )
  }

  get hasTrafficData() {
    return this.currentDelivery && this.currentDelivery.config.hasTrafficData
  }

  get hasCampaignCreation() {
    return (
      this.currentDelivery && this.currentDelivery.config.hasCampaignCreation
    )
  }

  get visitorsSum() {
    if (!this.mobilityStats) {
      return null
    }
    return this.hasCampaignCreation
      ? this.mobilityStats.visitorsSum
      : this.mobilityStats.visitorsAbs
  }

  get reachSumWidgetData() {
    return buildSingleStatWidget(
      this.visitorsSum,
      '/assets/img/icons/positive-clear/iso-walking.svg',
      `Total ${this.keyIndicatorLabel}`,
    )
  }

  get reachAvgWidgetData() {
    return buildSingleStatWidget(
      this.mobilityStats?.visitorsAvg,
      '/assets/img/icons/positive-clear/iso-walking.svg',
      'Avg. Reach per POI',
    )
  }

  get impressionsWidgetData() {
    if (this.hasTrafficData) return []
    return buildSingleStatWidget(
      this.mobilityStats?.visits,
      '/assets/img/icons/positive-clear/iso-walking.svg',
      'Avg. Impressions per POI',
    )
  }

  get trafficWidgetData() {
    if (!this.hasTrafficData) return []
    return buildSingleStatWidget(
      this.mobilityStats?.traffic,
      '/assets/img/icons/positive-clear/iso-by-car.svg',
      'Vehicles impacted',
    )
  }

  get widgetReferenceLabel() {
    return environment.politis ? 'Camp. reference' : 'Reference'
  }

  get widgetComparedLabel() {
    return environment.politis ? 'Camp. compared' : 'Compared'
  }

  constructor(
    protected authService: AuthenticationService,
    protected context: ContextService,
    protected sanitizer: DomSanitizer,
    protected changes: ChangeDetectorRef,
    protected mobilityService: MobilityService,
    protected isolinesService: IsolinesService,
    protected translateService: TranslateService,
    protected titleService: Title,
    protected http: HttpClient,
    protected zone: NgZone,
  ) {
    super(
      authService,
      context,
      sanitizer,
      changes,
      mobilityService,
      isolinesService,
      translateService,
      titleService,
      http,
      zone,
    )
  }

  async ngOnInit() {
    this.generalLegendColors = Colors.campaignLegend

    this.context.keyIndicatorLabels = {
      nvisitors: 'Reach',
      nvisits: 'Impressions',
    }
    this.context.setupKeyIndicators()
    this.context.keyIndicator.label =
      this.context.keyIndicatorLabels[this.context.keyIndicator.value]

    this.isLoading = true
    this.mapLoaded = false
    if (!this.currentBasemap) {
      this.basemapList.forEach((elem) => {
        elem.active = false
      })
      this.currentBasemap = this.basemapList[0]
      this.currentBasemap.active = true
    }
    this.setupBrandIcon()
    this.changeEventComponentState
      .asObservable()
      .pipe(
        debounceTime(100),
        switchMap(() => {
          this.isLoading =
            this.poisQuery.isFetching() ||
            this.aggregationsQuery.isFetching() ||
            this.compareAggregationsQuery.isFetching()
          this._resetComponent()
          this.changes.markForCheck()
          const promise = Promise.all([
            this._processStats(),
            this._processMap(),
          ]).then(() => {
            this.isLoading = false
            this.changes.markForCheck()
          })
          return of(promise)
        }),
      )
      .subscribe()

    this.subscriptions.add(
      merge(
        this.context.currentKeyIndicator,
        this.context.filterChanges,
      ).subscribe(() => {
        const columns = this.getColumnsFromTarget()
        this.aggregationsFilters.set({
          ...this.aggregationsFilters(),
          columns,
        })
        if (this.selectedCompareDelivery) {
          this.compareAggregationsFilters.set({
            ...this.compareAggregationsFilters(),
            columns,
          })
        }
        if (this.mapLoaded) {
          this.doComponentChanges()
        } else {
          this.changes.markForCheck()
        }
      }),
    )
  }

  ngOnDestroy() {
    this.context.keyIndicatorLabels = {
      nvisitors: 'Visitors',
      nvisits: 'Visits',
    }
    this.context.keyIndicator.label =
      this.context.keyIndicatorLabels[this.context.keyIndicator.value]
    super.ngOnDestroy()
  }

  protected async initLayers() {
    const map = this.map.map

    await loadCampaignMarkerIcons(map)

    this.clusterLayer = new OOHClusterLayer({
      onClick: (ev) => this.onMapClick(ev),
      onMouseEnter: (ev) => this.createHoverPopup(ev),
      onMouseOut: () => this.hideHoverPopup(),
    })

    this.clusterLayer.sources[0].clusterMaxZoom = 11
    // Add compare fields to cluster aggregation
    this.clusterLayer.sources[0].clusterProperties = {
      ...this.clusterLayer.sources[0].clusterProperties,
      belongs_to_d1: ['+', ['get', 'belongs_to_d1']],
      belongs_to_d2: ['+', ['get', 'belongs_to_d2']],
    }

    // Override points layer to use campaign markers (larger)
    ;(this.clusterLayer.layers as any)[2] = {
      id: this.clusterLayer.layers[2].id,
      source: this.clusterLayer.layers[2].source,
      type: 'symbol',
      filter: this.clusterLayer.layers[2].filter,
      layout: {
        'icon-image': 'campaign-marker-high',
        'icon-size': 0.6,
        'icon-allow-overlap': true,
      },
    }

    this.clusterLayer.init(map)

    const rows =
      this.poisQuery.data() ??
      (await firstValueFrom(this.poisData$.pipe(filter(Boolean))))

    if (rows?.length) {
      this._loadPoisData(rows)
    }
  }

  async _processMap(): Promise<any> {
    // After basemap change the cluster layer was destroyed — recreate it
    if (!this.clusterLayer) {
      await this.initLayers()
    }

    // Streets are painted reactively by streetsEffect when data arrives.
    // On subsequent calls (campaign change), update if source already exists.
    if (this.hasTrafficData && this.streetsQuery.data()) {
      this.paintDataTrafficLayer()
    }

    this._processPoiList()

    // flyToBbox here covers the page-refresh case where poisEffect fired
    // before the map was ready
    if (!this.initialFlyDone && this.currentCampaign?.territory?.bbox) {
      this.initialFlyDone = true
      this.map.flyToBbox(this.currentCampaign.territory.bbox, {
        padding: 150,
        pitch: 0,
        speed: 0.6,
      })
    }
  }

  protected getMap(delivery: Delivery = null) {
    if (!delivery) {
      delivery = this.currentCampaign
    }
    return this.mobilityService.getMap({
      useGeneralMap: true,
      delivery,
    })
  }

  removeDataTrafficLayer() {
    if (this.map.map) {
      if (this.map.map.getLayer(this.trafficDataId)) {
        this.map.map.removeLayer(this.trafficDataId)
      }
      if (this.map.map.getSource(this.trafficDataId)) {
        this.map.map.removeSource(this.trafficDataId)
      }
    }
  }

  paintDataTrafficLayer() {
    const stops = this.trafficQuantilesQuery.data()
    const geojson = this.streetsQuery.data()

    if (!geojson || !stops) return

    const source = this.map.map.getSource(this.trafficDataId)

    if (source) {
      ;(source as any).setData(geojson)
    } else {
      this.map.map.addSource(this.trafficDataId, {
        type: 'geojson',
        data: geojson,
      })

      let before = ''
      if (this.clusterLayer) {
        const clusterLayerId = this.clusterLayer.layers[0].id
        if (this.map.map.getLayer(clusterLayerId)) {
          before = clusterLayerId
        }
      }

      this.map.map.addLayer(
        {
          id: this.trafficDataId,
          type: 'line',
          source: this.trafficDataId,
          paint: {
            'line-width': 2,
            'line-opacity': 1,
            'line-color': {
              property: 'nvisitors',
              stops,
            },
          },
        },
        before,
      )
    }
  }

  setupBrandIcon() {
    this._brand = 'asset'
  }

  onMapClick(ev) {}

  // POIs are managed via TanStack Query (poisQuery effect), not getTerritoryList()
  _processPoi() {
    return Promise.resolve()
  }

  _loadPoisData(rows: PoiRow[]) {
    this.allPois = parsePoiRows(rows)
    this.poiList = buildPoiListItems(this.allPois, this.brand)

    this._processPoiList()
    this.changes.detectChanges()
  }

  _processPoiList() {
    if (this.clusterLayer) {
      let pois = this.allPois
      if (this.showOnlyCommonPOIs && this.selectedCompareDelivery) {
        const sharedIds = new Set(this.sharedCampaignPOIs.map((p) => p.id))
        pois = this.allPois.filter((p) => sharedIds.has(p.id))
      }
      this.clusterLayer.loadData(pois, !this.selectedPOI)
      this.updateCampaignMarkerStyle()
    }
  }

  private updateCampaignMarkerStyle() {
    const map = this.map?.map
    const layerId = this.clusterLayer?.layers[2]?.id
    if (!map || !layerId || !map.getLayer(layerId)) return

    const indicator = this.keyIndicator || 'nvisitors'
    const expression = buildQuartileMarkerExpression(this.allPois, indicator)
    if (!expression) return

    map.setLayoutProperty(layerId, 'icon-image', expression)
  }

  private updatePoisFilters() {
    if (!this.currentCampaign) return
    this.poisFilters.set({
      ...this.poisFilters(),
      delivery_id: Number(this.currentCampaign.id),
      delivery_start_date: this.currentCampaign.start,
    })

    this.streetsFilters.set({
      ...this.streetsFilters(),
      delivery_id: Number(this.currentCampaign.parent_id),
    })

    const columns = this.getColumnsFromTarget()
    this.aggregationsFilters.set({
      ...this.aggregationsFilters(),
      delivery_id: Number(this.currentCampaign.id),
      delivery_start_date: this.currentCampaign.start,
      columns,
    })
    this.demographicsFilters.set({
      ...this.demographicsFilters(),
      delivery_id: Number(this.currentCampaign.id),
      delivery_start_date: this.currentCampaign.start,
    })
  }

  _processStats(): Promise<any> {
    const currentIndicator = this.context.keyIndicator.value
    const currentIndicatorLabel = this.translateService.instant(
      this.context.keyIndicator.label,
    )
    const pois = this.poisQuery.data() || []

    const rankingResult = buildRanking({
      pois,
      currentIndicator,
      currentIndicatorLabel,
      campaignName: this.currentCampaign?.name,
      isVisitsAvailable: this.context.isVisitsAvailable(),
    })

    // Add compared campaign POIs as second ranking entry
    const comparePois = this.comparePoisQuery.data()
    if (comparePois?.length && this.selectedCompareDelivery) {
      const compareRanking = buildRanking({
        pois: comparePois,
        currentIndicator,
        currentIndicatorLabel,
        campaignName: this.selectedCompareDelivery.name,
        isVisitsAvailable: this.context.isVisitsAvailable(),
      })
      rankingResult.ranking.data.push(...compareRanking.ranking.data)
    }

    const visitorStats = buildVisitorStats({
      aggs: this.aggregationsQuery.data(),
      compareRows: this.compareAggregationsQuery.data(),
      currentCampaignId: Number(this.currentCampaign?.id),
      hasCompareDelivery: !!this.selectedCompareDelivery,
      pois,
    })

    this.mobilityStats = {
      ...this.mobilityStats,
      ...visitorStats,
      visits: null,
      legend: rankingResult.legend,
      ranking: rankingResult.ranking,
    }

    const demo = this.demographicsQuery.data()
    if (demo) {
      const compDemo = this.compareDemographicsQuery.data()
      const demographics = buildDemographicsStats(
        demo,
        compDemo,
        !!this.selectedCompareDelivery && !!compDemo,
      )
      Object.assign(this.mobilityStats, demographics)
    }

    return Promise.resolve()
  }

  // Override to read Mapbox GL properties instead of CARTO VL variables
  getVariableFromFeature(feature, key) {
    if (!feature) return null

    const keyReplacements = {
      count: 'point_count',
      ntrips: this.keyIndicator,
      ntrips_other: `${this.keyIndicator}_other`,
    }

    if (keyReplacements[key]) {
      key = keyReplacements[key]
    }

    const value = feature.properties && feature.properties[key]
    if (value === 'null') return null
    return value
  }

  // Fully overrides parent createHoverPopup because OOHClusterLayer uses
  // Mapbox GL features (feat.properties) instead of CARTO VL (feat.variables)
  createHoverPopup(ev) {
    const position = ev.position || ev.point
    if (!position) return

    const feat = ev.features?.[0]
    if (!feat) return

    const popupMargin = 12
    const id = this.getVariableFromFeature(feat, 'id') || feat.id
    const count = this.getVariableFromFeature(feat, 'count')
    const ntrips = this.getVariableFromFeature(feat, 'ntrips') || 0
    const ntrips_other = this.getVariableFromFeature(feat, 'ntrips_other') || 0
    const isCluster = Number(count) > 1
    const name = isCluster ? `${count} POIs` : String(id).split(':').pop()

    const nvars = [
      {
        value: ntrips,
        label: this.context.keyIndicator?.label || 'Reach',
        key: this.keyIndicator,
      },
    ]

    const data: any = {
      id,
      name,
      ntrips,
      ntrips_other,
      nvars,
      keyIndicator: this.keyIndicator,
    }

    this.hoverPopupData = {
      top: position.y + popupMargin,
      left: position.x + popupMargin,
      data,
      show: true,
      primary: !this.selectedPOI,
      showFrom: false,
      tooltip: null,
      tooltipExtra: null,
      updated: false,
    }

    // In compare mode, show comparison breakdown in the popup
    if (this.selectedCompareDelivery) {
      if (isCluster) {
        this.createClusterPopup(ev)
      } else {
        // Single POI — check if it belongs to both deliveries
        const belongs_to_d1 = this.getVariableFromFeature(feat, 'belongs_to_d1')
        const belongs_to_d2 = this.getVariableFromFeature(feat, 'belongs_to_d2')
        if (belongs_to_d1 && belongs_to_d2) {
          this.createRepeatedPOIPopup(ev)
        } else {
          this.createComparisonPopup({
            ntrips: belongs_to_d1 ? ntrips : 0,
            ntrips_other: belongs_to_d2 ? ntrips_other : 0,
            belongs_to_d1: belongs_to_d1 ? 1 : 0,
            belongs_to_d2: belongs_to_d2 ? 1 : 0,
          })
        }
      }
    }

    this.changes.detectChanges()
  }

  hideHoverPopup = () => {
    this.hoverPopupData.show = false
    this.changes.detectChanges()
  }

  createRepeatedPOIPopup(ev) {
    const feat = ev.features[0]
    const ref = this.campaignPOIs.find((c) => c.id === feat.id)
    const comp = this.comparedCampaignPOIs.find((c) => c.id === feat.id)

    const ntrips = ref[this.keyIndicator]
    const ntrips_other = comp[this.keyIndicator]
    const belongs_to_d1 = 1
    const belongs_to_d2 = 1
    this.createComparisonPopup({
      ntrips,
      ntrips_other,
      belongs_to_d1,
      belongs_to_d2,
    })
  }

  createClusterPopup(ev) {
    const feat = ev.features[0]
    const ntrips = this.getVariableFromFeature(feat, 'ntrips')
    const ntrips_other = this.getVariableFromFeature(feat, 'ntrips_other')
    const belongs_to_d1 = this.getVariableFromFeature(feat, 'belongs_to_d1')
    const belongs_to_d2 = this.getVariableFromFeature(feat, 'belongs_to_d2')
    this.createComparisonPopup({
      ntrips,
      ntrips_other,
      belongs_to_d1,
      belongs_to_d2,
    })
  }

  createMultipleFeaturesPopup(ev) {
    const refFeats = ev.features.filter(
      (f) => this.getVariableFromFeature(f, 'ntrips') > 0,
    )
    const compFeats = ev.features.filter(
      (f) => this.getVariableFromFeature(f, 'ntrips_other') > 0,
    )
    const ntrips = sum(
      refFeats.map((f) => this.getVariableFromFeature(f, 'ntrips')),
    )
    const ntrips_other = sum(
      compFeats.map((f) => this.getVariableFromFeature(f, 'ntrips_other')),
    )
    const belongs_to_d1 = refFeats.length
    const belongs_to_d2 = compFeats.length
    this.createComparisonPopup({
      ntrips,
      ntrips_other,
      belongs_to_d1,
      belongs_to_d2,
    })
  }

  createComparisonPopup({
    ntrips,
    ntrips_other,
    belongs_to_d1,
    belongs_to_d2,
  }) {
    this.zone.run(() => {
      this.hoverPopupData.data.name = this.translateService.instant(
        this.keyIndicatorLabel,
      )
      this.hoverPopupData.data.ntrips = ntrips + ntrips_other
      this.hoverPopupData.data.ntrips_other = null
      this.hoverPopupData.data.reach = ntrips
      this.hoverPopupData.data.reach_other = ntrips_other

      this.hoverPopupData.data.belongs_to_d1 = belongs_to_d1
      this.hoverPopupData.data.belongs_to_d2 = belongs_to_d2

      this.hoverPopupData.data.nvars = [
        {
          key: this.keyIndicator,
          label: 'Total',
          value: ntrips + ntrips_other,
        },
      ]

      this.hoverPopupData.tooltipExtra = [
        { label: 'Reference', class: 'label primary' },
        { label: 'POIs', value: 'belongs_to_d1', class: 'bold' },
        { label: 'Reach', value: 'reach' },
        { label: 'Compared', class: 'label secondary' },
        { label: 'POIs', value: 'belongs_to_d2', class: 'bold' },
        { label: 'Reach', value: 'reach_other' },
      ]
    })
  }

  toggleDeleteCampaignModal() {
    this.showDeleteConfirmModal = true
  }

  deleteCampaign() {
    // Number() coercion because CampaignRow.id has no runtime validation —
    // BigQuery may return it as a string despite the TypeScript interface.
    const idToDelete = Number(this.currentCampaign.id)
    this.deleteCampaignLoading = true
    this.deleteCampaignMutation.mutate(idToDelete, {
      onSuccess: () => {
        const newCampaign = this.campaigns.find((c) => c.id !== idToDelete)
        this.deliveryChange(newCampaign)
        this.campaigns = this.campaigns.filter((c) => c.id !== idToDelete)
        this.deleteCampaignLoading = false
        this.showDeleteConfirmModal = false
      },
      onError: (err) => {
        console.error('[campaigns.component.ts] Error deleting campaign', err)
        this.deleteCampaignLoading = false
        this.showDeleteConfirmModal = false
      },
    })
  }

  deliveryChange(delivery) {
    this.currentCampaign = delivery
    this.updatePoisFilters()
    // Compare filters that reference the primary (delivery_id_1) must follow
    // the new primary; otherwise the compare queries keep returning data for
    // the previous (primary, compared) pair, leaving stats and POI layers
    // stale until the user touches the compared selector.
    if (this.selectedCompareDelivery) {
      this.compareAggregationsFilters.set({
        ...this.compareAggregationsFilters(),
        delivery_id_1: Number(this.currentCampaign.id),
        delivery_start_date_1: this.currentCampaign.start,
      })
      this.compareDeliveriesFilters.set({
        ...this.compareDeliveriesFilters(),
        delivery_id_1: Number(this.currentCampaign.id),
        delivery_start_date_1: this.currentCampaign.start,
      })
    }
    this.doComponentChanges()
  }

  deliveryToCompareSelected(delivery) {
    super.deliveryToCompareSelected(delivery)

    if (delivery) {
      const columns = this.getColumnsFromTarget()
      this.compareAggregationsFilters.set({
        delivery_id_1: Number(this.currentCampaign.id),
        delivery_id_2: Number(delivery.id),
        delivery_start_date_1: this.currentCampaign.start,
        delivery_start_date_2: delivery.start,
        filter_combined: this.compareAggregationsFilters().filter_combined,
        columns,
      })
      this.compareDemographicsFilters.set({
        delivery_id: Number(delivery.id),
        delivery_start_date: delivery.start,
        filter_combined: this.demographicsFilters().filter_combined,
      })
      this.comparePoisFilters.set({
        delivery_id: Number(delivery.id),
        delivery_start_date: delivery.start,
        filter_combined: this.poisFilters().filter_combined,
        tag_groups: this.poisFilters().tag_groups,
      })
      this.compareDeliveriesFilters.set({
        delivery_id_1: Number(this.currentCampaign.id),
        delivery_start_date_1: this.currentCampaign.start,
        delivery_id_2: Number(delivery.id),
        delivery_start_date_2: delivery.start,
        filter_combined: this.poisFilters().filter_combined,
        tag_groups: this.poisFilters().tag_groups,
      })
    } else {
      // Set compareMode=false BEFORE signal changes trigger demographicsEffect,
      // so the effect's detectChanges() renders widgets with isMultiple=false
      this.compareMode = false
      this.compareAggregationsFilters.set({
        ...this.compareAggregationsFilters(),
        delivery_id_2: 0,
      })
      this.compareDemographicsFilters.set({
        ...this.compareDemographicsFilters(),
        delivery_id: 0,
      })
      this.comparePoisFilters.set({
        ...this.comparePoisFilters(),
        delivery_id: 0,
      })
      this.compareDeliveriesFilters.set({
        ...this.compareDeliveriesFilters(),
        delivery_id_2: 0,
        delivery_start_date_2: '',
      })
      // Restore original POI data (without compare fields) into the cluster layer
      const originalPois = this.poisQuery.data()
      if (originalPois?.length) {
        this._loadPoisData(originalPois)
      }
      // Explicitly reset demographics to single format to avoid stale compare data
      const demo = this.demographicsQuery.data()
      if (demo) {
        const demographics = buildDemographicsStats(demo, null, false)
        this.mobilityStats = { ...this.mobilityStats, ...demographics }
        this.changes.detectChanges()
      }
    }

    this.doComponentChanges()
  }

  changeBasemap(item) {
    try {
      this.cleanCartoLayers()
    } catch (e) {}

    this.currentBasemap = item
    // Don't call initMap() — onMapLoaded() fires after style.load,
    // triggering doComponentChanges() → _processMap() which re-creates the cluster layer
  }

  cleanCartoLayers() {
    this.clusterLayer.destroy()
    delete this.clusterLayer

    if (this.hasTrafficData) {
      this.removeDataTrafficLayer()
    }
  }

  openRankingList() {
    this.showRankingPopup = true
  }

  async downloadCSV() {
    try {
      const dayHour = await this.fetchDayHourBreakdown()
      downloadCampaignCSV({
        mobilityStats: this.mobilityStats,
        keyIndicator: this.keyIndicator,
        keyIndicatorLabel: this.keyIndicatorLabel,
        campaignName: this.currentCampaign.name,
        campaignId: this.currentCampaign.id,
        dayHour,
      })
    } catch (err) {
      console.error(
        '[campaigns.component.ts] Error converting json to csv',
        err,
      )
    }
  }

  private async fetchDayHourBreakdown() {
    const base = this.aggregationsFilters()
    if (!base.delivery_id) return undefined
    const dayCombos = ['Weekday-All', 'Weekend-All']
    const hourCombos = [
      'All-Morning',
      'All-Afternoon',
      'All-Evening',
      'All-Night',
    ]
    const all = [...dayCombos, ...hourCombos]
    const results = await Promise.all(
      all.map((fc) =>
        fetchAggregations(this.cartoClient, {
          ...base,
          filter_combined: fc,
        }),
      ),
    )
    const [weekday, weekend, morning, afternoon, evening, night] = results
    return {
      day: {
        Weekday: weekday?.sum ?? 0,
        Weekend: weekend?.sum ?? 0,
      },
      hour: {
        Morning: morning?.sum ?? 0,
        Afternoon: afternoon?.sum ?? 0,
        Evening: evening?.sum ?? 0,
        Night: night?.sum ?? 0,
      },
    }
  }

  onToggleCommonPOIs() {
    this.showOnlyCommonPOIs = !this.showOnlyCommonPOIs
    this._processPoiList()
  }

  private getColumnsFromTarget(): string[] {
    return getColumnsFromTargetFilter(this.context.filters['target'])
  }
}
